import { useAppSelector, useBreakPoint } from "@hooks";
import { config, useTransition } from "@react-spring/web";
import {
  getBuildingById,
  getFloorById,
  getFloorHasMap,
  getAreaById,
  getIsOccupancyEnabled,
  getNodeInformationPerAreaByBuildingId,
  AreaWithSensorInfo,
  useAppDispatch,
  withAsyncThunkErrorHandling,
  AreaResource,
} from "@lib/store";
import { useTranslation } from "react-i18next";
import {
  InactiveStyledIcon,
  StyledButton,
  StyledParagraph,
  StyledSubtitle,
  StyledUserCell,
  WorkspaceInformationWrapper,
} from "./styles";
import { HaNModalHeader, HaNWorkspaceModalPage } from "@organisms";
import { StyledBody } from "../HaNRoomInformation/styles";
import { CapacityIndicator, MapView } from "@molecules";
import { getDeskAmenityIcon, pxToRem } from "@utils";
import { HaNWorkspaceIcon } from "@organisms";
import { useEffect, useMemo, useState } from "react";
import { FlexCol, FlexRow, H1, H2, H4, P, Subtitle } from "@quarks";
import { Icon } from "@atoms";
import { createState } from "@lib/utils";
import isToday from "date-fns/isToday";
import startOfDay from "date-fns/startOfDay";
import { HaNControlsSection } from "../HaNControlsSection";
import { format } from "date-fns";
import {
  dateKeyForDateTimeString,
  dateToLocalDateTimeString,
  localDateStringToEndOfDay,
  timeLabel,
} from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useConnectionReservationsWithUsers } from "@lib/store/src/ui/hooks/timeslots/useConnectionReservationsWithUsers";
import { actions } from "@lib/store/src/data/timeslots/actions";
import { ReservationCount } from "@lib/store/src/data/timeslots/apiTypes";
import { LocationBasedServices } from "../../LocationBasedServices";

type HaNWorkspaceInformationProps = {
  onClose: () => void;
  workspace: AreaWithSensorInfo | AreaResource;
  setPage: (pageName: HaNWorkspaceModalPage) => void;
  page: HaNWorkspaceModalPage;
  showMapTile?: "mobile_only" | "always";
};

const isResource = (workspace: AreaWithSensorInfo | AreaResource): workspace is AreaResource => {
  return (workspace as AreaResource).data !== undefined;
};

export const HaNWorkspaceInformation = ({
  workspace,
  onClose,
  setPage,
  page,
  showMapTile = "mobile_only",
}: HaNWorkspaceInformationProps) => {
  const { t } = useTranslation();
  const date = new Date();
  const floorHasMap = useAppSelector(getFloorHasMap(workspace.floorId));
  const { name, buildingId, floorId, id: areaId } = workspace;

  const activityTypes = isResource(workspace) ? workspace.data.activityTypes : workspace.activityTypes;
  const deskAmenities = isResource(workspace) ? workspace.data.deskAmenities : workspace.deskAmenities;
  const hasBookableDesks = isResource(workspace)
    ? workspace.data.workspaceBookingPolicyType === "DeskBooking"
    : workspace.hasBookableDesks;

  const sensorInformation = isResource(workspace) ? undefined : workspace.sensorInformation;
  const sensorUpdateTimeLabel = isResource(workspace) ? undefined : workspace.sensorUpdateTimeLabel;
  const workspaceBookingPolicyType = isResource(workspace)
    ? workspace.data.workspaceBookingPolicyType
    : workspace.workspaceBookingPolicyType;

  workspace;
  const capacity = isResource(workspace) ? workspace.data.capacity : workspace.capacity || workspace.adHocCapacity;

  const selectedAreaState = useMemo(() => createState(workspace ? [workspace.id] : [], "selected"), [workspace]);
  const area = useAppSelector(state => getAreaById(state, areaId))!;
  const areaBookingStates = useAppSelector(state =>
    getNodeInformationPerAreaByBuildingId(state, startOfDay(new Date()).toISOString(), buildingId),
  );
  const building = useAppSelector(state => getBuildingById(state, buildingId));
  const isOccupancyEnabled = useAppSelector(getIsOccupancyEnabled);
  const floor = useAppSelector(state => getFloorById(state, floorId));
  const connectionsData = useConnectionReservationsWithUsers(format(date, "yyyy-MM-dd")).filter(data => {
    return "areaId" in data.reservation.workspace && data.reservation.workspace.areaId === workspace.id;
  });

  // Store a count internally because it's a value that has a short lifespan
  const [reservationCount, setReservationCount] = useState<ReservationCount | null>(null);
  // Ensure we never show a count for the wrong area
  if (reservationCount && reservationCount.areaId !== workspace.id) setReservationCount(null);

  // Note: this relies on re-renders triggered because of other stuff to trigger
  const fetchPrecision = 1000 * 60 * 5; // Every unique 5 minute interval
  const nearest5MinTimestamp = Math.round(Date.now() / fetchPrecision) * fetchPrecision;
  const dispatch = useAppDispatch();
  useEffect(
    function updateAvailabilityInfo() {
      let canceled = false;
      const now = dateToLocalDateTimeString(new Date(nearest5MinTimestamp));

      const fetchAvailability = async () => {
        const result = await dispatch(
          withAsyncThunkErrorHandling(() =>
            actions.fetchWorkspaceAvailabilityForNodeId({
              nodeId: workspace.id,
              localStart: now,
              localEnd: localDateStringToEndOfDay(dateKeyForDateTimeString(now)),
            }),
          ),
        );
        if (canceled) return;

        if (result.success) {
          const forThisArea = result.result.find(c => c.areaId === workspace.id);
          setReservationCount(forThisArea ?? null);
        }
      };

      fetchAvailability();
      return () => {
        canceled = true;
      };
    },
    [nearest5MinTimestamp, dispatch, workspace.id],
  );

  const deskFullyBooked = reservationCount ? reservationCount.booked >= area.capacity : false;

  const breakPoint = useBreakPoint();
  const showMapThumbnail =
    (floorHasMap && breakPoint === "small" && showMapTile === "mobile_only") ||
    (floorHasMap && showMapTile === "always");
  const areaInfo = (areaBookingStates ?? {})[area.id];

  const transition = useTransition(page === "workspaceInformation", {
    initial: { transform: "translate3d(0%, 0, 0)" },
    from: { transform: "translate3d(-100%, 0, 0)" },
    enter: { transform: "translate3d(0%, 0, 0)" },
    leave: { transform: "translate3d(-100%, 0, 0)" },
    config: { ...config.stiff, clamp: true },
  });

  return transition(
    (style, item) =>
      item && (
        <WorkspaceInformationWrapper
          data-testid="HaNWorkspaceInformation-MainWrapper"
          style={style}>
          <HaNModalHeader
            onClose={onClose}
            title={t("workplace:Area")}
          />
          <StyledBody as="section">
            {building?.name || floor?.name ? (
              <FlexCol
                gap={16}
                data-testid="HaNWorkspaceInfromation-BuildingAndFloorNameWrapper">
                <H1 as="h2">{name}</H1>
                <P>{[t("meeting:MeetingRoomSeat", { count: capacity }), building?.name, floor?.name].join(", ")}</P>
              </FlexCol>
            ) : null}
            <FlexCol gap={16}>
              <H2 as="h3">{t("screen:InviteesAvailability")}</H2>

              {sensorInformation && (
                <FlexRow
                  gap={16}
                  alignItems="center"
                  justifyContent="flex-start">
                  <HaNWorkspaceIcon iconType={sensorInformation.status} />
                  <FlexCol>
                    <H4>{sensorInformation?.title}</H4>
                    <Subtitle>{sensorUpdateTimeLabel}</Subtitle>
                  </FlexCol>
                </FlexRow>
              )}

              {!sensorInformation && isOccupancyEnabled ? (
                <FlexRow
                  gap={16}
                  alignItems="center"
                  justifyContent="flex-start">
                  <InactiveStyledIcon
                    icon="noBroadcast"
                    size="24px"
                  />
                  <FlexCol>
                    <H4>{t("NoSensorInstalled")}</H4>
                  </FlexCol>
                </FlexRow>
              ) : null}

              <FlexRow
                gap={16}
                alignItems="center"
                justifyContent="flex-start">
                {workspaceBookingPolicyType === "NonBookable" ? (
                  <FlexRow
                    gap={16}
                    alignItems="center">
                    <InactiveStyledIcon
                      icon="noCalendar"
                      size="24px"
                    />
                    <H4>{t("hereAndNow:AdHocUse")}</H4>
                  </FlexRow>
                ) : (
                  <>
                    <InactiveStyledIcon
                      icon="calendar"
                      size="24px"
                    />
                    <FlexCol>
                      <H4>{t(hasBookableDesks ? "screen:DeskDetail" : "hereAndNow:AreaBooking")}</H4>
                      <FlexRow gap={8}>
                        <CapacityIndicator
                          capacity={area.capacity}
                          occupation={reservationCount?.booked ?? 0}
                          displayDescription={false}
                        />
                        <StyledSubtitle $alertState={deskFullyBooked}>
                          {!deskFullyBooked
                            ? `${area.capacity - (reservationCount?.booked ?? 0)}/${area.capacity} ${t(
                                "workplace:WorkspaceGroupAvailable",
                              )}`
                            : `${t("translation:FullyBooked")}`}
                        </StyledSubtitle>
                      </FlexRow>
                    </FlexCol>
                  </>
                )}
              </FlexRow>

              {sensorInformation && (
                <StyledButton
                  button="text"
                  onClick={() => setPage("availabilityInformation")}>
                  {t("hereAndNow:LearnMore")}
                </StyledButton>
              )}
            </FlexCol>
            {activityTypes.length ? (
              <FlexCol
                gap={16}
                data-testid="HaNWorkspaceInformation-ActivityTypesWrapper">
                <H2 as="h3">{t("workplace:ActivityTypesFilterSectionTitle")}</H2>
                <StyledParagraph>
                  {activityTypes.map(activity => t(`activityTypes:activityType`, { context: activity })).join(", ")}
                </StyledParagraph>
              </FlexCol>
            ) : null}
            {deskAmenities.length ? (
              <FlexCol
                gap={16}
                data-testid="HaNWorkspaceInformation-DeskAmenitiesWrapper">
                <H2 as="h3">{t("deskAmenities:deskAmenity_other")}</H2>
                <P>{t("workplace:DeskAmenities")}</P>
                {deskAmenities.map(amenity => {
                  return (
                    <FlexRow
                      gap={8}
                      key={amenity}
                      alignItems="center"
                      justifyContent="flex-start">
                      <Icon
                        weight="thin"
                        size={pxToRem(24)}
                        icon={getDeskAmenityIcon(amenity)}></Icon>
                      <P>{t(`deskAmenities:deskAmenity`, { context: amenity })}</P>
                    </FlexRow>
                  );
                })}
              </FlexCol>
            ) : null}

            <HaNControlsSection nodeId={workspace.id} />

            {/* TODO: PUT BACK MAP */}
            {showMapThumbnail && (
              <FlexCol gap={16}>
                <H2 as="h3">{t("translation:LocationTitle")}</H2>
                <MapView
                  buildingId={buildingId}
                  floorId={floorId}
                  buildingNodeStates={selectedAreaState}
                  borderRadius={8}
                  fullView={false}
                  highlights={[
                    {
                      type: "area",
                      nodeId: workspace.id,
                      available: areaInfo
                        ? areaInfo.capacity - areaInfo.occupation
                        : area.capacity || area.adHocCapacity,
                      connections: [],
                    },
                  ]}
                  disablePointerEvents={true}
                  data-testid="HaNWorkspaceInformation-Map"
                />
              </FlexCol>
            )}
            {connectionsData.length ? (
              <FlexCol gap={16}>
                <H2 as="h3">{t("connection:ConnectionsThatBookedHere")}</H2>
                {connectionsData.map(({ user, reservation }) => {
                  return (
                    <StyledUserCell
                      shouldShowCheckIn={isToday(date)}
                      user={user}
                      subtitle={timeLabel(reservation.localStart, reservation.localEnd, t)}
                      key={reservation.id}
                    />
                  );
                })}
              </FlexCol>
            ) : null}

            <LocationBasedServices
              buildingId={buildingId}
              locationType="Area"
              locationName={name}
            />
          </StyledBody>
        </WorkspaceInformationWrapper>
      ),
  );
};
